import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import CustomerProfileService from './CustomerProfileService';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    maxHeight: '90vh',
    width: '480px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflow: 'auto',
  },
};

/**
 * Price-negotiation slots for one platform customer (Mercari + PayPay together).
 * Automatic rule (backend): 2, or 5 for a tier above the lowest / lifetime
 * spending + wallet ≥ 30,000. The admin can set a fixed number or unlimited —
 * the automatic rule never overwrites either.
 */
const NegotiationQuotaModal = ({ isOpen, onClose, profile }) => {
  const [data, setData] = useState(null);
  const [limitInput, setLimitInput] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const applyResponse = useCallback((next) => {
    setData(next);
    setLimitInput(next.negotiationLimit == null ? '' : String(next.negotiationLimit));
    setUnlimited(next.negotiationUnlimited);
  }, []);

  useEffect(() => {
    if (!isOpen || !profile?.id) return;
    setData(null);
    CustomerProfileService.getNegotiationQuota(profile.id)
      .then((res) => applyResponse(res.data.data))
      .catch((err) => toast.error(err.response?.data?.message || 'โหลดสิทธิ์ต่อราคาไม่สำเร็จ'));
  }, [isOpen, profile?.id, applyResponse]);

  const handleSave = async () => {
    // Empty = back to the automatic rule.
    const trimmed = limitInput.trim();
    const negotiationLimit = trimmed === '' ? null : Number(trimmed);
    if (negotiationLimit !== null && (!Number.isInteger(negotiationLimit) || negotiationLimit < 0 || negotiationLimit > 999)) {
      toast.error('จำนวนต้องเป็นเลขจำนวนเต็ม 0-999 หรือเว้นว่างเพื่อใช้ค่าอัตโนมัติ');
      return;
    }
    setIsSaving(true);
    try {
      const res = await CustomerProfileService.updateNegotiationQuota(profile.id, {
        negotiationLimit,
        negotiationUnlimited: unlimited,
      });
      applyResponse(res.data.data);
      toast.success('บันทึกสิทธิ์ต่อราคาแล้ว');
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setIsSaving(false);
    }
  };

  const quota = data?.quota;

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles} ariaHideApp={false}>
      <div className="p-6">
        <h2 className="mb-1 text-2xl font-bold">สิทธิ์ขอลดราคา (Mercari + PayPay)</h2>
        <p className="mb-4 text-sm text-gray-500">{profile?.customerName || profile?.smileName}</p>

        {!data ? (
          <p className="text-sm text-gray-500">กำลังโหลด...</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="text-sm text-gray-600">ใช้อยู่ตอนนี้</div>
              <div className="text-2xl font-bold">
                {quota.unlimited ? `${quota.used} / ไม่จำกัด` : `${quota.used} / ${quota.limit}`}
              </div>
              {!quota.unlimited && (
                <div className="text-sm text-gray-600">เหลือ {quota.remaining} รายการ</div>
              )}
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={unlimited}
                onChange={(e) => setUnlimited(e.target.checked)}
              />
              <span className="font-medium">ไม่จำกัด (SUPER VIP)</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-900">กำหนดจำนวนเอง</label>
              <input
                type="number"
                min={0}
                max={999}
                value={limitInput}
                onChange={(e) => setLimitInput(e.target.value)}
                disabled={unlimited}
                placeholder="เว้นว่าง = อัตโนมัติ (2 หรือ 5)"
                className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm ${unlimited ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
              />
              <p className="mt-1 text-xs text-gray-500">
                อัตโนมัติ: 2 รายการ หรือ 5 รายการเมื่อระดับสมาชิกสูงกว่าระดับเริ่มต้น
                หรือยอดสั่งซื้อสะสม + ยอดเงินใน Wallet ตั้งแต่ 30,000 บาท
              </p>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="w-full rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-gray-400"
            >
              {isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-md bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
        >
          ปิด
        </button>
      </div>
    </Modal>
  );
};

export default NegotiationQuotaModal;
